import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../api/supabase'

export interface DailyChecklist {
  id: string
  location: string
  checklist_date: string
  completed_by: string
  // Z64.2: which job this checklist actually verified, so it's traceable to a specific
  // shipment rather than just "checked, somehow". Required in the UI, not at the DB layer.
  job_ref: string | null
  overall_result: string | null
  notes: string | null
  created_by: string | null
  created_at: string
}

export interface ChecklistResponse {
  id: string
  checklist_id: string
  section: string
  item_description: string
  response: string
  notes: string | null
}

export const calcDailyResult = (responses: { response: string }[]): string => {
  return responses.some(r => r.response === 'no') ? 'fail' : 'pass'
}

export function useETSFDaily(location?: string) {
  const [checklists, setChecklists] = useState<DailyChecklist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadChecklists = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      let query = supabase
        .from('etsf_daily_checklists')
        .select('*')
        .order('checklist_date', { ascending: false })

      if (location) {
        query = query.eq('location', location)
      }

      const { data, error: qErr } = await query
      if (qErr) throw qErr

      setChecklists((data || []) as DailyChecklist[])
    } catch (e: any) {
      setError(e.message || 'Failed to load checklists')
    } finally {
      setLoading(false)
    }
  }, [location])

  useEffect(() => {
    loadChecklists()
  }, [loadChecklists])

  const createChecklist = useCallback(
    async (checklistData: Partial<DailyChecklist>, responses: Partial<ChecklistResponse>[]) => {
      const { data: checklist, error: checklistErr } = await supabase
        .from('etsf_daily_checklists')
        .insert([{
          ...checklistData,
          overall_result: calcDailyResult(responses as any)
        }])
        .select()
        .single()

      if (checklistErr) throw checklistErr

      const responsesWithChecklistId = responses.map(r => ({
        ...r,
        checklist_id: checklist.id
      }))

      const { error: responsesErr } = await supabase
        .from('etsf_checklist_responses')
        .insert(responsesWithChecklistId)

      if (responsesErr) throw responsesErr

      await loadChecklists()
      return checklist as DailyChecklist
    },
    [loadChecklists]
  )

  const fetchChecklist = useCallback(async (id: string) => {
    const { data, error } = await supabase
      .from('etsf_daily_checklists')
      .select('*')
      .eq('id', id)
      .single()
    if (error) throw error
    return data as DailyChecklist
  }, [])

  const fetchResponses = useCallback(async (checklistId: string) => {
    const { data, error } = await supabase
      .from('etsf_checklist_responses')
      .select('*')
      .eq('checklist_id', checklistId)
      .order('section', { ascending: true })
    if (error) throw error
    return (data || []) as ChecklistResponse[]
  }, [])

  return {
    checklists,
    loading,
    error,
    reload: loadChecklists,
    createChecklist,
    fetchChecklist,
    fetchResponses,
  }
}
